import { useMemo } from 'react'

export interface SelectOption {
  value: string
  label: string
}

/** Generate locale-aware time options in 10-minute increments (144 total). */
export function buildTimeOptions(locale: string): SelectOption[] {
  const fmt = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
  const refDate = new Date(2000, 0, 1)
  const opts: SelectOption[] = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 10) {
      const h = hour.toString().padStart(2, '0')
      const m = minute.toString().padStart(2, '0')
      refDate.setHours(hour, minute, 0, 0)
      opts.push({
        value: `${h}:${m}`,
        label: fmt.format(refDate),
      })
    }
  }
  return opts
}

/** Memoized locale-aware time options hook. */
export const useTimeOptions = (locale: string) =>
  useMemo(() => buildTimeOptions(locale), [locale])

interface DateOptionsConfig {
  locale: string
  pbxTimeZone: string
  includeRelativeLabels?: boolean
  todayLabel?: string
  tomorrowLabel?: string
}

/**
 * Generate date options for the next 7 days in the PBX timezone.
 */
export const useDateOptions = ({
  locale,
  pbxTimeZone,
  includeRelativeLabels = false,
  todayLabel = 'Today',
  tomorrowLabel = 'Tomorrow',
}: DateOptionsConfig) =>
  useMemo(() => {
    const options: SelectOption[] = []

    const pbxDateFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: pbxTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const todayParts = pbxDateFmt.formatToParts(new Date())
    const pY = Number(todayParts.find((p) => p.type === 'year')?.value)
    const pM = Number(todayParts.find((p) => p.type === 'month')?.value)
    const pD = Number(todayParts.find((p) => p.type === 'day')?.value)
    const anchorMs = Date.UTC(pY, pM - 1, pD, 12, 0, 0)

    const weekdayFmt = new Intl.DateTimeFormat(locale, {
      timeZone: pbxTimeZone,
      weekday: 'short',
    })
    const monthDayFmt = new Intl.DateTimeFormat(locale, {
      timeZone: pbxTimeZone,
      month: 'short',
      day: 'numeric',
    })

    for (let i = 0; i < 7; i++) {
      const dayInstant = new Date(anchorMs + i * 86_400_000)
      const parts = pbxDateFmt.formatToParts(dayInstant)
      const y = parts.find((p) => p.type === 'year')?.value
      const m = parts.find((p) => p.type === 'month')?.value
      const d = parts.find((p) => p.type === 'day')?.value
      const value = `${y}-${m}-${d}`

      let dayLabel: string
      if (includeRelativeLabels && i === 0) {
        dayLabel = todayLabel
      } else if (includeRelativeLabels && i === 1) {
        dayLabel = tomorrowLabel
      } else {
        dayLabel = weekdayFmt.format(dayInstant)
      }
      const dateLabel = includeRelativeLabels
        ? monthDayFmt.format(dayInstant)
        : ''

      options.push({
        value,
        label: dateLabel ? `${dayLabel} ${dateLabel}` : dayLabel,
      })
    }
    return options
  }, [locale, pbxTimeZone, includeRelativeLabels, todayLabel, tomorrowLabel])

/**
 * Smart time filter: supports "1pm", "2am", "730pm", "7:30pm", "10am", etc.
 * Falls back to plain substring matching.
 */
export const filterTimeOptions = (
  filter: string,
  options: SelectOption[],
): SelectOption[] => {
  if (!filter) return options
  const trimmed = filter.trim()

  // Smart parse: "1pm", "2am", "730pm", "7:30pm", "10am", "12:00", etc.
  const match = trimmed.match(/^(\d{1,2}):?(\d{2})?\s*(a|am|p|pm)?$/i)
  if (match) {
    const hour = parseInt(match[1], 10)
    const minutes = match[2] ? parseInt(match[2], 10) : null
    const period = match[3]?.toLowerCase()

    const results = options.filter((opt) => {
      const [hStr, mStr] = opt.value.split(':')
      const h24 = parseInt(hStr, 10)
      const m = parseInt(mStr, 10)
      const displayHour = h24 % 12 || 12
      const isAm = h24 < 12

      if (period && (period === 'a' || period === 'am') !== isAm) return false
      if (displayHour !== hour) return false
      if (minutes !== null && m !== minutes) return false
      return true
    })
    if (results.length > 0) return results
  }

  // Fallback: plain substring match
  const q = trimmed.toLowerCase().replace(/\s+/g, '')
  return options.filter((opt) =>
    opt.label.toLowerCase().replace(/\s+/g, '').includes(q),
  )
}
